import React, {
    useContext, useEffect, useMemo, useRef, useState 
} from 'react';
import {
    Box, Typography, 
    useTheme
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CardContainer from '../../components/cardContainer';
import Card from '../../components/card';
import {
    DiagnosticFormContext 
} from '../../../../contexts/form';
import {
    TypographyVariants, TypographyWeights 
} from '../../../../figmaTheme';

export const GoalsSection = (): JSX.Element => {
    const [skipAnimation, setSkipAnimation] = useState(false);
    const useStyles = useMemo(() => makeStyles((theme) => ({
        scrollableContainer: {
            overflow: 'scroll',
            scrollbarWidth: 'none',
            minHeight: '100%',
            width: '100vw',
            maxWidth: '692px',
            transform: 'translate(-24px, 0)',
            padding: '0 24px'
        },
        content: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
        },
        sections: {
            height: '80px',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            columnGap: '30px',
            marginTop: '50px',
            transform: 'translate(0, 16.2px)',
            animation: `$squeeze ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1.75}s`,
        },
        sectionText: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            columnGap: '20px',
            transform: 'translate(0, -3.8px)',
        },
        section: {
            height: '24px',
            width: '24px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            rowGap: '8px'
        },
        background: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.palette.neutral['100'],
            borderRadius: '50%',
            width: '100%',
            height: '100%',
            flexShrink: 0
        },
        goalsBackground: {
            backgroundColor: theme.palette.neutral['100'],
            animation: `$changeColor ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 2.75}s`,
        },
        icon: {
            display: 'flex',
            width: '50%',
            height: '50%',
            fill: theme.palette.white,
            animation: `$fadeOut ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1.75}s`
        },
        text: {
            textAlign: 'center',
            width: '35px',
        },
        goalsSection: {
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards 0s, $shrink ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1.75}s, $grow ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 2.75}s, $moveSection ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 2.75}s`,
        },
        goalText: {
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards 0s, $textFade ${skipAnimation ? 0 : 0.25}s forwards ${skipAnimation ? 0 : 1.5}s`,
        },
        goalsIcon: {
            animation: `$fadeOut ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1.75}s, $fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 2.75}s, $growIcon ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 2.75}s`,
        },
        habitsSection: {
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 0.2}s, $shrink ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1.75}s, $fadeOut ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 2.75}s`,
        },
        habitsText: {
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards 0s, $textFade ${skipAnimation ? 0 : 0.25}s forwards ${skipAnimation ? 0 : 1.5}s`,
        },
        aboutYouSection: {
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 0.4}s, $shrink ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1.75}s, $fadeOut ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 2.75}s`,
        },
        aboutYouText: {
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 0.4}s, $textFade ${skipAnimation ? 0 : 0.25}s forwards ${skipAnimation ? 0 : 1.5}s`,
        },
        planSection: {
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 0.6}s, $shrink ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1.75}s, $fadeOut ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 2.75}s`,
        },
        planText: {
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 0.6}s, $textFade ${skipAnimation ? 0 : 0.25}s forwards ${skipAnimation ? 0 : 1.5}s`,
        },
        targetSection: {
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 0.8}s, $shrink ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1.75}s, $fadeOut ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 2.75}s`,
        },
        targetText: {
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 0.8}s, $textFade ${skipAnimation ? 0 : 0.25}s forwards ${skipAnimation ? 0 : 1.5}s`,
        },
        title: {
            fontFamily: 'Poppins',
            fontWeight: TypographyWeights.BOLD,
            textAlign: 'center',
            marginBottom: '8px',
            marginTop: '50px',
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 3.25}s`
        },
        subtitle: {
            fontFamily: 'Poppins',
            textAlign: 'center',
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 3.25}s`
        },
        '@keyframes fadeIn': {
            '0%': {
                opacity: '0' 
            },
            '100%': {
                opacity: '1' 
            },
        },
        '@keyframes fadeOut': {
            '0%': {
                opacity: '1' 
            },
            '100%': {
                opacity: '0' 
            },
        },
        '@keyframes shrink': {
            '0%': {
                width: '24px',
                height: '24px' 
            },
            '100%': {
                width: '16px',
                height: '16px' 
            },
        },
        '@keyframes squeeze': {
            '0%': {
                columnGap: '30px' 
            },
            '100%': {
                columnGap: '10px' 
            },
        },
        '@keyframes textFade': {
            '0%': {
                opacity: '1' 
            },
            '100%': {
                opacity: '0' 
            },
        },
        '@keyframes changeColor': {
            '0%': {
                backgroundColor: theme.palette.neutral['100'] 
            },
            '100%': {
                backgroundColor: theme.palette.primary.main 
            },
        },
        '@keyframes grow': {
            '0%': {
                width: '24px',
                height: '24px' 
            },
            '100%': {
                width: '80px',
                height: '80px' 
            },
        },
        '@keyframes growIcon': {
            '0%': {
                width: '50%',
                height: '50%' 
            },
            '100%': {
                width: '67.5%',
                height: '67.5%' 
            },
        },
        '@keyframes moveSection': {
            '0%': {
                transform: 'translate(0)' 
            },
            '100%': {
                transform: 'translate(52px, 0)' 
            },
        },
    })), [skipAnimation]);
    const formContext = useContext(DiagnosticFormContext);
    const classes = useStyles();
    const theme = useTheme();
    const [textHeight, setTextHeight] = useState(null);
    const textSection = useRef(null);

    useEffect(() => {
        if (!textSection.current) return;

        setTextHeight(textSection.current.clientHeight);
    }, [textSection]);

    const onClick = () => {
        if (!skipAnimation) setSkipAnimation(true);
        else formContext.goNext();
    };

    useEffect(() => {
        setTimeout(() => {
            setSkipAnimation(true);
        }, 3750);
    }, []);

    const onBackClick = () => {
        formContext.goBack();
    };

    const canGoBack = useMemo(() => {
        const challengeSection = formContext.sections.find(section => section.key === 'challenge');
        const introSection = formContext.sections.find(section => section.key === 'intro');
        return !!challengeSection || !!introSection;
    }, [formContext.sections]);

    return (
        <CardContainer
            sectionKey={'goals'}
            goBack={canGoBack ? onBackClick : null}
            goNext={onClick}
            backgroundColor={theme.palette.white}
            hideHeaderText
        >
            <Card>
                <Box className={classes.scrollableContainer}>
                    <Box className={classes.content}>
                        <Box className={classes.sections}>
                            <Box className={`${classes.section} ${classes.goalsSection}`}>
                                <Box className={`${classes.background} ${classes.goalsBackground}`}>
                                    <Box className={`${classes.icon} ${classes.goalsIcon}`}>
                                        {formContext.sections.find(section => section.key === 'goals')?.icon}
                                    </Box>
                                </Box>
                            </Box>
                            <Box className={`${classes.section} ${classes.habitsSection}`}>
                                <Box className={classes.background}>
                                    <Box className={classes.icon}>
                                        {formContext.sections.find(section => section.key === 'habits')?.icon}
                                    </Box>
                                </Box>
                            </Box>
                            <Box className={`${classes.section} ${classes.aboutYouSection}`}>
                                <Box className={classes.background}>
                                    <Box className={classes.icon}>
                                        {formContext.sections.find(section => section.key === 'aboutYou')?.icon}
                                    </Box>
                                </Box>
                            </Box>
                            <Box className={`${classes.section} ${classes.planSection}`}>
                                <Box className={classes.background}>
                                    <Box className={classes.icon}>
                                        {formContext.sections.find(section => section.key === 'plan')?.icon}
                                    </Box>
                                </Box>
                            </Box>
                            <Box className={`${classes.section} ${classes.targetSection}`}>
                                <Box className={classes.background}>
                                    <Box className={classes.icon}>
                                        {formContext.sections.find(section => section.key === 'checkout')?.icon}
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                        <Box
                            className={classes.sectionText}
                            ref={textSection}
                        >
                            <Typography
                                variant={TypographyVariants.VERY_SMALL}
                                className={`${classes.text} ${classes.goalText}`}
                                style={{
                                    transform: 'translate(2px, 0)' 
                                }}
                            >
                                {'Goals'}
                            </Typography>
                            <Typography
                                variant={TypographyVariants.VERY_SMALL}
                                className={`${classes.text} ${classes.habitsText}`}
                                style={{
                                    transform: 'translate(1px, 0)' 
                                }}
                            >
                                {'Habits'}
                            </Typography>
                            <Typography
                                variant={TypographyVariants.VERY_SMALL}
                                className={`${classes.text} ${classes.aboutYouText}`}
                            >
                                {'About'}
                            </Typography>
                            <Typography
                                variant={TypographyVariants.VERY_SMALL}
                                className={`${classes.text} ${classes.planText}`}
                                style={{
                                    transform: 'translate(-1px, 0)' 
                                }}
                            >
                                {'Plan'}
                            </Typography>
                            <Typography
                                variant={TypographyVariants.VERY_SMALL}
                                className={`${classes.text} ${classes.targetText}`}
                                style={{
                                    transform: 'translate(-1px, 0)' 
                                }}
                            >
                                {'Start'}
                            </Typography>
                        </Box>
                        <Typography
                            variant={TypographyVariants.H2}
                            className={classes.title}
                            style={{
                                transform: `translate(0, ${-textHeight + 16.2}px)` 
                            }}
                        >
                            {'Your Goals'}
                        </Typography>
                        <Typography
                            variant={TypographyVariants.H5}
                            className={classes.subtitle}
                            style={{
                                transform: `translate(0, ${-textHeight + 16.2}px)` 
                            }}
                        >
                            {'We\'ll start by learning about your high-level goals for healthier drinking.'}
                        </Typography>
                    </Box>
                </Box>
            </Card>
        </CardContainer>
    );
};

export default GoalsSection;
